<script setup lang="ts">
import { useSustainableDevelopment } from '@/domains/esg/composables/content/useSustainableDevelopment'
const {
  significanceMatrixClimateChange,
  significanceMatrixOwnEmployeeResources,
  significanceMatrixConsumersAndEndUsers,
  significanceMatrixAffectedCommunities,
  significanceMatrixBusinessConduct,
} = useSustainableDevelopment()

</script>

<template>
  <UiSection
    id="list-of-material-topics"
    class="significant-matrix"
    color="white"
  >
    <UiHeader
      position="center"
      :header="$t('sustainableDevelopment.significanceMatrix.header')"
      :subheader="$t('sustainableDevelopment.significanceMatrix.subheader')"
    />

    <div class="section__container">
      <div class="section__row __legend">
        <div class="section__col">
          <div class="matrix-legend">
            <h3 class="matrix-legend__heading">
              <i :class="significanceMatrixClimateChange.label.type">{{ significanceMatrixClimateChange.label.key }}</i>
              
              {{ significanceMatrixClimateChange.header }}
            </h3>

            <ul
              v-for="(items, i) in significanceMatrixClimateChange.items"
              :key="`significance-matrix-list-${i}`"
              class="matrix-legend__list"
            >
              <li
                v-for="(item, j) in items"
                :key="'significance-matrix-list-item' + j"
                class="matrix-legend__list-item"
              >
                {{ item.text }}
              </li>
            </ul>
          </div>

          <div class="matrix-legend">
            <h3 class="matrix-legend__heading">
              <i :class="significanceMatrixOwnEmployeeResources.label.type">{{ significanceMatrixOwnEmployeeResources.label.key }}</i>
              
              {{ significanceMatrixOwnEmployeeResources.header }}
            </h3>

            <ul
              v-for="(items, i) in significanceMatrixOwnEmployeeResources.items"
              :key="`significance-matrix-list-${i}`"
              class="matrix-legend__list"
            >
              <li
                v-for="(item, j) in items"
                :key="'significance-matrix-list-item' + j"
                class="matrix-legend__list-item"
              >
                {{ item.text }}
              </li>
            </ul>
          </div>

          <div class="matrix-legend __affected-communities">
            <h3 class="matrix-legend__heading">
              <i :class="significanceMatrixAffectedCommunities.label.type">{{ significanceMatrixAffectedCommunities.label.key }}</i>
              
              {{ significanceMatrixAffectedCommunities.header }}
            </h3>

            <ul
              v-for="(items, i) in significanceMatrixAffectedCommunities.items"
              :key="`significance-matrix-list-${i}`"
              class="matrix-legend__list"
            >
              <li
                v-for="(item, j) in items"
                :key="'significance-matrix-list-item' + j"
                class="matrix-legend__list-item"
              >
                {{ item.text }}
              </li>
            </ul>
          </div>

          <div class="matrix-legend __consumers-and-end-users">
            <h3 class="matrix-legend__heading">
              <i :class="significanceMatrixConsumersAndEndUsers.label.type">{{ significanceMatrixConsumersAndEndUsers.label.key }}</i>
              
              {{ significanceMatrixConsumersAndEndUsers.header }}
            </h3>

            <ul
              v-for="(items, i) in significanceMatrixConsumersAndEndUsers.items"
              :key="`significance-matrix-list-${i}`"
              class="matrix-legend__list"
            >
              <li
                v-for="(item, j) in items"
                :key="'significance-matrix-list-item' + j"
                class="matrix-legend__list-item"
              >
                {{ item.text }}
              </li>
            </ul>
          </div>

          <div class="matrix-legend __business-conduct">
            <h3 class="matrix-legend__heading">
              <i :class="significanceMatrixBusinessConduct.label.type">{{ significanceMatrixBusinessConduct.label.key }}</i>
              
              {{ significanceMatrixBusinessConduct.header }}
            </h3>

            <ul
              v-for="(items, i) in significanceMatrixBusinessConduct.items"
              :key="`significance-matrix-list-${i}`"
              class="matrix-legend__list"
            >
              <li
                v-for="(item, j) in items"
                :key="'significance-matrix-list-item' + j"
                class="matrix-legend__list-item"
              >
                {{ item.text }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="section__row">
        <div class="section__col">
          <NuxtLink
            external
            target="_blank"
            class="matrix-legend_link"
            :to="$t('sustainableDevelopment.significanceMatrix.link.url')"
          >
            {{ $t('sustainableDevelopment.significanceMatrix.link.text') }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </UiSection>
</template>

<style lang="scss" scoped>

:deep(.ui-header__subheader) {
  margin-bottom: 3.2rem;
}

.significant-matrix {
  :deep(.ui-header__header) {
    margin-bottom: 9rem;
  }

  :deep(.ui-header__subheader) {
    br {
      display: none;
    }
  }

  :deep(.section__row.__matrix) {
    margin-bottom: 4.8rem;
  }

  :deep(.section__row.__legend) {
    gap: 0;
    padding-top: 4.8rem;
  }
}

.matrix-legend {
   margin-bottom: 1.6rem;
   border-bottom: 1px solid $gray-300;

  &__heading {
    @include body-1-medium;
    display: flex;
    flex-direction: column;
    margin-bottom: 1.6rem;
  }

  i {
    @include body-2-medium;
    display: inline-block;
    width: 5.8rem;
    height: 3.2rem;
    margin-right: 1.6rem;
    margin-bottom: 1.6rem;
    padding: 0.4rem 1.6rem;
    font-style: normal;
    text-align: center;
    background-color: $gray-500;
    border-radius: 3.2rem;

    &.social {
      color: $white;
      background: linear-gradient(90deg, #A265F0 0%, #C39BF5 100%);
    }

    &.environmental {
      color: $white;
      background: linear-gradient(90deg, #3B7BF5 0%, #6CA4FA 100%);
    }

    &.governance {
      color: #065D86;
      background: linear-gradient(90deg, #78CBFA 0%, #B3E2FD 100%);
    }
  }

  &__list {
    margin-left: 2.5rem;
    padding: 0;
    padding-bottom: 1.6rem;
    list-style: none;
    list-style: disc;

    &-item {
      @include body-1;
      padding-bottom: 0.4rem;
      color: $text-300;
      line-height: 3.2rem;
    }
  }

  &_link {
    @include body-1;
    display: block;
    text-align: center;
    text-decoration: underline;
  }

  &.__business-conduct {
    &:last-of-type {
      border: none;
    }
  }
}

@include atM {
  :deep(.ui-header) {
    margin-bottom: 9rem;
  }

  .matrix-legend {
    &__heading {
      flex-direction: row;
    }

    &__list {
      margin-left: 9.5rem;
    }

    i {
      margin-bottom: 0;
    }
  }

  .significant-matrix {
    :deep(.section__row.__matrix) {
      margin-bottom: 9.6rem;
    }

    :deep(.section__row.__legend) {
      gap: 6.4rem;
      padding-top: 9.6rem;
    }

    :deep(.ui-header__subheader) {
      br {
        display: inline;
      }
    }
  }
}
</style>
